import { ErrorCode, FileRejection } from 'react-dropzone';

import * as diagnostics from '@/utils/diagnostics';

const BYTES_PER_MB = 1024 * 1024;
// Match the server's 256 MiB file limit; the upload UI labels this unit MB.
const MAX_UPLOAD_SIZE_MB = 256;
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * BYTES_PER_MB;

export enum CustomErrorCode {
  EncryptedPdf = 'encrypted',
}

export enum FileUploadErrorCode {
  EmptyFile = 'EMPTY_FILE',
  HashFailed = 'HASH_FAILED',
  RegistrationFailed = 'REGISTRATION_FAILED',
  UploadFailed = 'UPLOAD_FAILED',
}

const FILE_UPLOAD_ERROR_MESSAGES: Record<FileUploadErrorCode, string> = {
  [FileUploadErrorCode.EmptyFile]:
    'A selected file was corrupted. Please check the file and try again.',
  [FileUploadErrorCode.HashFailed]:
    "We couldn't process this file. Please check the file and try again.",
  [FileUploadErrorCode.RegistrationFailed]:
    "We weren't able to register your file. Please try again.",
  [FileUploadErrorCode.UploadFailed]:
    "We weren't able to upload your file. Please try again.",
};

export class FileUploadError extends Error {
  public readonly code: FileUploadErrorCode;
  public readonly userMessage: string;
  public override readonly cause?: Error;

  constructor(code: FileUploadErrorCode, cause?: Error) {
    const userMessage = FILE_UPLOAD_ERROR_MESSAGES[code];
    super(userMessage);
    this.name = 'FileUploadError';
    this.code = code;
    this.userMessage = userMessage;
    this.cause = cause;
  }
}

export function getFileUploadErrorMessage(error: unknown): string {
  if (error instanceof FileUploadError) {
    return error.userMessage;
  }
  return "We weren't able to upload your file. Please try again.";
}

export function isExpectedFileUploadError(error: unknown): boolean {
  return (
    error instanceof FileUploadError &&
    error.code === FileUploadErrorCode.EmptyFile
  );
}

/**
 * Maps over `items` running at most `limit` tasks concurrently, returning a
 * settled result per item so one failure doesn't discard the rest of the batch.
 * Uploading a large batch all at once (e.g. 99 files) trips browser/proxy
 * connection limits, and the dropped requests surface as "Failed to fetch".
 */
export async function mapWithConcurrency<Item, Result>(
  items: Item[],
  limit: number,
  task: (item: Item) => Promise<Result>
): Promise<PromiseSettledResult<Result>[]> {
  const results: PromiseSettledResult<Result>[] = new Array(items.length);
  let cursor = 0;

  async function worker() {
    while (cursor < items.length) {
      const index = cursor++;
      // index is always a valid position: the guard above only lets us past it
      // while cursor < items.length, and each worker claims a distinct index.
      const item = items[index]!;
      try {
        results[index] = { status: 'fulfilled', value: await task(item) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  }

  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
}

/**
 * Uploads a file, retrying once if the upload itself fails. A dropped network
 * request ("Failed to fetch") is usually transient, so a single retry recovers
 * most of them. Deterministic failures (empty/unreadable file) are not retried.
 */
export async function uploadWithRetry<Result>(
  uploadFile: (file: File) => Promise<Result>,
  file: File
): Promise<Result> {
  try {
    return await uploadFile(file);
  } catch (error) {
    if (
      error instanceof FileUploadError &&
      error.code === FileUploadErrorCode.UploadFailed
    ) {
      return uploadFile(file);
    }
    throw error;
  }
}

const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// A plain DOCX is a ZIP file. Office stores a password-protected DOCX in a
// compound file instead, which starts with this signature.
const COMPOUND_FILE_SIGNATURE = [
  0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1,
];

function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

function isDocxFile(file: File): boolean {
  return (
    file.type === DOCX_MIME_TYPE || file.name.toLowerCase().endsWith('.docx')
  );
}

async function isEncryptedPdf(file: File): Promise<boolean> {
  const bytes = new Uint8Array(await file.slice(0, 2048).arrayBuffer());
  const isPdf =
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46;
  return isPdf && new TextDecoder().decode(bytes).includes('/Encrypt');
}

async function isEncryptedDocx(file: File): Promise<boolean> {
  const header = new Uint8Array(
    await file.slice(0, COMPOUND_FILE_SIGNATURE.length).arrayBuffer()
  );
  return COMPOUND_FILE_SIGNATURE.every((byte, index) => header[index] === byte);
}

/**
 * Checks if a PDF or DOCX file is password-protected.
 * If the browser cannot read the file, this returns false so that the upload
 * step reports the read error to the user.
 */
export async function isFileEncrypted(file: File): Promise<boolean> {
  try {
    if (isPdfFile(file)) return await isEncryptedPdf(file);
    if (isDocxFile(file)) return await isEncryptedDocx(file);
  } catch {
    return false;
  }
  return false;
}

export type BackgroundStyleState = 'normal' | 'error' | 'hover';

function buf2hex(buffer: ArrayBuffer) {
  return Array.prototype.map
    .call(new Uint8Array(buffer), (x: number) =>
      ('00' + x.toString(16)).slice(-2)
    )
    .join('');
}

export async function getSha256HashFromArrayBuffer(arrayBuffer: ArrayBuffer) {
  const sha256Hash = await crypto.subtle.digest('SHA-256', arrayBuffer);
  return buf2hex(sha256Hash);
}

export async function getSha256HashFromFile(file: File) {
  const arrayBuffer = await file.arrayBuffer();
  return getSha256HashFromArrayBuffer(arrayBuffer);
}

// https://react-dropzone.org/#!/Accepting%20specific%20file%20types
export const acceptedFileTypes = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/tiff': ['.tiff'],
  'image/gif': ['.gif'],
  'image/png': ['.png'],
  'image/bmp': ['.bmp'],
  'text/plain': ['.txt'],

  'application/vnd.apple.pages': ['.pages'],
  'application/vnd.apple.keynote': ['.key'],
  'application/vnd.apple.numbers': ['.numbers'],

  'application/pdf': ['.pdf'],

  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],

  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
    '.pptx',
  ],

  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],

  'application/csv': ['.csv'],
};

export function getErrorMessageFromCode(rejection: FileRejection): string {
  const firstError = rejection.errors[0];
  switch (firstError?.code) {
    case ErrorCode.FileInvalidType:
      return `The file you're trying to upload isn't a valid type.`;
    case ErrorCode.FileTooLarge:
      return `The file you uploaded is too large. The maximum size is ${MAX_UPLOAD_SIZE_MB}MB and your file was ${Math.ceil(rejection.file.size / BYTES_PER_MB)}MB.`;
    case ErrorCode.FileTooSmall:
      return `The file you're trying to upload is too small.`;
    case ErrorCode.TooManyFiles:
      return `Please upload one file at a time.`;
    case CustomErrorCode.EncryptedPdf:
      return `Password-protected PDFs are not allowed.`;
    default:
      diagnostics.error(
        `unhandled file upload error type`,
        new Error(`Unhandled file upload error type ${firstError?.code}`),
        {
          fileType: rejection.file.type,
          fileSize: rejection.file.size,
        }
      );
      return 'Upload error. Please try again.';
  }
}
